/**
 * O cartão de um módulo na tela inicial: ícone em cima, nome embaixo, um link.
 *
 * Papel: este é o `.mod-card` — fundo `--surface`, borda `--line-strong`,
 * subindo 2px e trocando para a borda `--brass` no hover, sem sombra em
 * nenhum dos dois estados.
 *
 * Como usar: mora em `shared/ui/` e não em `app/`, onde fica a tela inicial,
 * porque o submenu de Residências, dentro de `modules/residencias`, desenha o
 * mesmo cartão para os assuntos da Fazenda Boa Vista, e a fronteira do ESLint
 * só deixa um módulo alcançar `shared`, nunca `app`. Quem chama passa o
 * componente do ícone, nunca o elemento já montado — é este arquivo quem
 * decide o tamanho dele e aplica a transição de cor —, e o nome como já deve
 * aparecer na tela, em texto normal: a caixa alta do `.mod-name` é aplicada
 * aqui por CSS, então um nome passado já em maiúsculas sairia com o
 * `letter-spacing` de maiúsculas por cima de letras que já eram maiúsculas.
 *
 * Não há `aria-label`. O nome visível já é o texto que identifica o link para
 * quem usa leitor de tela, e repeti-lo num `aria-label` do tipo "Abrir módulo
 * X" faria o mesmo nome ser anunciado duas vezes seguidas.
 */
import { type ComponentType, type SVGProps } from 'react';
import { Link } from 'react-router';

import { cn } from '@/shared/lib/utils';

interface CartaoDeModuloProps {
  /** Caminho absoluto para onde o cartão navega ao ser aberto. */
  rota: string;
  /** Nome do módulo, em texto normal — o `text-transform: uppercase` é do CSS. */
  nome: string;
  /** Componente do ícone, desenhado em `currentColor` para herdar a cor do link. */
  Icone: ComponentType<SVGProps<SVGSVGElement>>;
  className?: string;
}

/**
 * O cartão de um módulo, com ícone e nome.
 *
 * @param rota - Caminho absoluto passado ao `Link`.
 * @param nome - Nome mostrado dentro do cartão.
 * @param Icone - Componente do ícone; é este arquivo que o instancia, sempre
 *   com o tamanho fixo de 30px.
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O cartão, como um `Link` inteiro clicável.
 */
export function CartaoDeModulo({ rota, nome, Icone, className }: CartaoDeModuloProps) {
  return (
    <Link
      to={rota}
      className={cn(
        'group flex min-h-[104px] flex-col justify-center gap-[10px] rounded-md',
        'border border-linha-forte bg-superficie p-[26px_24px]',
        'transition-[background-color,border-color,transform] duration-200 ease-in-out',
        'hover:-translate-y-0.5 hover:border-brass hover:bg-superficie-2',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brass-light',
        className,
      )}
    >
      <Icone className="size-[30px] text-brass transition-colors duration-200 ease-in-out group-hover:text-brass-light" />
      <span
        className={cn(
          'font-sans text-base font-semibold tracking-[0.03em] text-foreground uppercase',
          'transition-colors duration-200 ease-in-out group-hover:text-brass-light',
        )}
      >
        {nome}
      </span>
    </Link>
  );
}
